import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ChevronDown, Info } from "lucide-react";
import { T } from "../EditContext";
import { Heading } from "./Sections";

const BASE = process.env.REACT_APP_BACKEND_URL;
const cards = (p, key = "cards") => (p[key] || []).filter((c) => c.visible !== false);

// Premium accent palettes — Unani gold, Nutrition sapphire, Ayurveda herbal green.
// Styles live in index.css so they work in both the dark and light public themes.
const ACCENT = Object.fromEntries(["gold", "blue", "green", "neutral"].map((k) => [k, {
    card: `sci-card sci-card--${k}`,
    rule: `sci-rule sci-rule--${k}`,
    text: `sci-ink--${k}`,
    soft: `sci-soft--${k}`,
    badge: `sci-badge sci-badge--${k}`,
}]));
const acc = (key) => ACCENT[key] || ACCENT.neutral;

export const useScienceProducts = () => useQuery({
    queryKey: ["science-products"],
    queryFn: async () => (await fetch(`${BASE}/api/science/products`)).json(),
    staleTime: 30000,
});

export const useScienceSettings = () => useQuery({
    queryKey: ["science-settings"],
    queryFn: async () => (await fetch(`${BASE}/api/science/settings`)).json(),
    staleTime: 60000,
});

export const ScienceHeroSection = ({ props: p = {} }) => (
    <section className="mx-auto max-w-7xl px-6 md:px-12 pt-28 md:pt-36 pb-14" data-testid="science-hero">
        <div className="max-w-3xl">
            <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-4" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                <T f="title" value={p.title} />{" "}
                <T f="title_accent" value={p.title_accent} className="text-lime" />
            </h1>
            <T f="sub" value={p.sub} as="p" multiline className="mt-6 text-lg text-zinc-400" />
            <T f="body" value={p.body} as="p" multiline className="mt-4 text-zinc-500" />
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="science-pillars">
            {cards(p, "pillars").map((c, i) => {
                const a = acc(c.accent || (c.emphasis !== false ? "gold" : "neutral"));
                return (
                    <div key={c.title || i} data-testid={`science-pillar-${i}`}
                        className={`p-7 pt-8 hover:-translate-y-1 ${a.card}`}>
                        <span aria-hidden className={a.rule} />
                        <div className="flex items-start justify-between gap-3">
                            <div className={`mono text-[10px] tracking-[0.24em] ${a.text}`}>
                                {(c.tag || "").toUpperCase()}
                            </div>
                            {c.badge && (
                                <span data-testid={`science-pillar-badge-${i}`}
                                    className={`mono text-[9px] tracking-[0.16em] rounded-full px-2.5 py-1 shrink-0 ${a.badge}`}>
                                    {c.badge.toUpperCase()}
                                </span>
                            )}
                        </div>
                        <h2 className="font-display text-2xl uppercase tracking-tight mt-4">{c.title}</h2>
                        <p className="text-sm text-zinc-500 mt-3 leading-relaxed">{c.desc}</p>
                    </div>
                );
            })}
        </div>
    </section>
);

export const DualCardsSection = ({ props: p = {} }) => {
    const a = acc(p.accent);
    const right = acc(p.right_accent || p.accent);
    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-10 md:py-14" data-testid="science-dual-cards">
            <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />
            <Heading f="heading" value={p.heading} />
            <T f="body" value={p.body} as="p" multiline className="mt-4 text-zinc-500 max-w-3xl" />
            <div className={`mt-10 grid gap-4 ${p.columns === "2" ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                {cards(p).map((c, i) => {
                    const ca = c.accent ? acc(c.accent) : a;
                    return (
                        <div key={c.title || i} data-testid={`dual-card-${i}`}
                            className={`p-6 pt-7 hover:-translate-y-0.5 ${ca.card}`}>
                            <span aria-hidden className={ca.rule} />
                            {c.tag && <div className={`mono text-[9px] tracking-[0.2em] ${ca.text}`}>{c.tag.toUpperCase()}</div>}
                            <h3 className="font-display text-xl uppercase tracking-tight mt-2">{c.title}</h3>
                            {c.traditional && (
                                <div className="mt-4">
                                    <div className={`mono text-[9px] tracking-[0.18em] ${ca.text}`}>
                                        {(p.left_label || "Traditional concept").toUpperCase()}
                                    </div>
                                    <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{c.traditional}</p>
                                </div>
                            )}
                            {c.modern && (
                                <div className="mt-4 pt-4 border-t border-white/8">
                                    <div className={`mono text-[9px] tracking-[0.18em] ${p.right_accent ? right.text : "text-zinc-500"}`}>
                                        {(p.right_label || "Modern perspective").toUpperCase()}
                                    </div>
                                    <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{c.modern}</p>
                                </div>
                            )}
                            {c.desc && <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{c.desc}</p>}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export const ConceptFlowSection = ({ props: p = {} }) => {
    const a = acc(p.accent);
    const steps = cards(p, "steps");
    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-10 md:py-14" data-testid="science-flow">
            <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />
            <Heading f="heading" value={p.heading} />
            <T f="body" value={p.body} as="p" multiline className="mt-4 text-zinc-500 max-w-3xl" />
            <div className="mt-10 grid gap-4 md:grid-cols-4">
                {steps.map((s, i) => (
                    <div key={s.title || i} className="relative">
                        <div data-testid={`flow-step-${i}`} className={`h-full p-6 pt-7 ${a.card}`}>
                            <span aria-hidden className={a.rule} />
                            <div className={`font-display text-3xl ${a.soft}`}>{s.number || String(i + 1).padStart(2, "0")}</div>
                            <h3 className="font-display text-lg uppercase tracking-tight mt-3">{s.title}</h3>
                            <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{s.desc}</p>
                        </div>
                        {i < steps.length - 1 && (
                            <ArrowRight className={`hidden md:block absolute -right-3.5 top-1/2 w-5 h-5 ${a.soft}`} />
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
};

export const ComparisonSection = ({ props: p = {} }) => {
    const left = acc(p.accent);
    const right = acc(p.right_accent);
    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-10 md:py-14" data-testid="science-comparison">
            <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />
            <Heading f="heading" value={p.heading} />
            <T f="body" value={p.body} as="p" multiline className="mt-4 text-zinc-500 max-w-3xl" />
            <div className="mt-10 border border-white/10 overflow-hidden">
                <div className="grid grid-cols-2 bg-white/[0.04]">
                    <div className={`relative p-4 mono text-[10px] tracking-[0.2em] ${left.text}`}>
                        <span aria-hidden className={left.rule} />
                        {(p.left_label || "Unani perspective").toUpperCase()}
                    </div>
                    <div className={`relative p-4 mono text-[10px] tracking-[0.2em] border-l border-white/10 ${right.text}`}>
                        <span aria-hidden className={right.rule} />
                        {(p.right_label || "Modern nutritional perspective").toUpperCase()}
                    </div>
                </div>
                {cards(p, "rows").map((r, i) => (
                    <div key={r.left || i} className="grid grid-cols-2 border-t border-white/8" data-testid={`comparison-row-${i}`}>
                        <div className="p-4 text-sm">{r.left}</div>
                        <div className="p-4 text-sm text-zinc-400 border-l border-white/8">{r.right}</div>
                    </div>
                ))}
            </div>
            <p className="mt-4 text-xs text-zinc-500 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <T f="note" value={p.note || "A conceptual connection, not a claim that the two systems are scientifically identical."} multiline />
            </p>
        </section>
    );
};

const SYSTEM_LABEL = { unani: "Unani", ayurveda: "Ayurveda", nutrition: "Nutrition science", combination: "Unani + Nutrition" };

export const ProductScienceHubSection = ({ props: p = {} }) => {
    const { data: items = [], isLoading } = useScienceProducts();
    const { data: settings } = useScienceSettings();
    const limit = p.limit && Number(p.limit) > 0 ? Number(p.limit) : items.length;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-10 md:py-14" data-testid="science-hub">
            <T f="eyebrow" value={p.eyebrow || settings?.hub_eyebrow} as="div" className="eyebrow mb-3" />
            <Heading f="heading" value={p.heading || settings?.hub_heading} />
            <T f="sub" value={p.sub || settings?.hub_sub} as="p" multiline className="mt-4 text-zinc-500 max-w-3xl" />
            {isLoading ? (
                <div className="mt-10 mono text-xs text-zinc-500">LOADING…</div>
            ) : (
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="science-hub-grid">
                    {items.slice(0, limit).map((item, i) => (
                        <Link key={item.slug} to={`/science/${item.slug}`} data-testid={`science-card-${item.slug}`}
                            className="group flex flex-col border border-white/10 bg-bg1 p-6 hover:border-lime/50 hover:-translate-y-1 transition-all">
                            <div className="flex items-start justify-between gap-3">
                                <span className="font-display text-2xl text-gold/40">{String(i + 1).padStart(2, "0")}</span>
                                {item.product.status !== "active" ? (
                                    <span className="mono text-[9px] tracking-[0.16em] text-gold border border-gold/40 px-2 py-1">
                                        COMING SOON
                                    </span>
                                ) : (
                                    <span className="mono text-[9px] tracking-[0.16em] text-lime border border-lime/40 px-2 py-1">
                                        AVAILABLE
                                    </span>
                                )}
                            </div>
                            {item.product.images?.[0] ? (
                                <img src={item.product.images[0]} alt={item.product.name} loading="lazy"
                                    className="mt-4 h-36 object-contain self-center group-hover:scale-105 transition-transform" />
                            ) : (
                                <div data-testid={`science-card-placeholder-${item.slug}`}
                                    className="mt-4 h-36 flex flex-col items-center justify-center border border-dashed border-white/12 bg-white/[0.02]">
                                    <span className="font-display text-3xl text-gold/50 tracking-tight">
                                        {(item.product.name || "").split(" ").map((w) => w[0]).join("").slice(0, 3).toUpperCase()}
                                    </span>
                                    <span className="mono text-[9px] tracking-[0.16em] text-zinc-600 mt-2">PACK SHOT COMING SOON</span>
                                </div>
                            )}
                            <h3 className="font-display text-2xl uppercase tracking-tight mt-5">{item.product.name}</h3>
                            <div className="mono text-[9px] tracking-[0.18em] text-zinc-500 mt-1.5">
                                {[item.product.product_type, item.category].filter(Boolean).join(" · ").toUpperCase()}
                            </div>
                            <p className="text-sm text-zinc-500 mt-3 leading-relaxed flex-1">{item.positioning}</p>
                            <div className="mt-4 flex flex-wrap gap-1.5">
                                <span className="mono text-[9px] tracking-[0.14em] text-gold border border-gold/30 px-2 py-1">
                                    {(SYSTEM_LABEL[item.traditional_system] || item.traditional_system || "").toUpperCase()}
                                </span>
                                <span className="mono text-[9px] tracking-[0.14em] text-zinc-500 border border-white/10 px-2 py-1">
                                    {item.ingredient_count} INGREDIENTS
                                </span>
                            </div>
                            <span className="mt-5 pt-4 border-t border-white/8 mono text-[10px] tracking-[0.18em] text-lime inline-flex items-center gap-1.5">
                                EXPLORE SCIENCE <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </Link>
                    ))}
                </div>
            )}
        </section>
    );
};

export const ScienceDisclaimerSection = ({ props: p = {} }) => {
    const { data: settings } = useScienceSettings();
    const text = p.text || settings?.disclaimer || "";
    if (!text) return null;
    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 pb-20" data-testid="science-disclaimer">
            <div className="border border-white/10 bg-white/[0.02] p-6 flex items-start gap-3">
                <Info className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <T f="text" value={text} as="p" multiline className="text-xs text-zinc-500 leading-relaxed" />
            </div>
        </section>
    );
};

// Combos stay collapsed so the page never dumps every ingredient at once.
export const ComboExplorer = ({ combos, ingredients, basisLabel = "per tablet" }) => {
    const [open, setOpen] = useState(null);
    return (
        <div className="grid md:grid-cols-3 gap-4" data-testid="combo-explorer">
            {combos.map((c) => {
                const rows = ingredients.filter((r) => (r.combos || []).includes(c.id));
                const isOpen = open === c.id;
                return (
                    <div key={c.id} className="border border-white/10 bg-bg1 p-6" data-testid={`combo-card-${c.id}`}>
                        <div className="font-display text-3xl" style={{ color: c.color || "#7CB342" }}>{c.number}</div>
                        <h3 className="font-display text-xl uppercase tracking-tight mt-2">{c.name}</h3>
                        <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{c.description}</p>
                        <button onClick={() => setOpen(isOpen ? null : c.id)} data-testid={`combo-toggle-${c.id}`}
                            className="mt-5 mono text-[10px] tracking-[0.18em] text-lime inline-flex items-center gap-1.5">
                            {isOpen ? "HIDE INGREDIENTS" : `EXPLORE INGREDIENTS (${rows.length})`}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isOpen && (
                            <ul className="mt-4 pt-4 border-t border-white/8 space-y-2" data-testid={`combo-ingredients-${c.id}`}>
                                {rows.map((r) => (
                                    <li key={r.id || r.name} className="flex items-baseline justify-between gap-3 text-sm">
                                        <span>{r.name}</span>
                                        <span className="mono text-[10px] text-zinc-500 shrink-0">
                                            {r.dose} {r.unit}
                                        </span>
                                    </li>
                                ))}
                                <li className="mono text-[9px] tracking-[0.14em] text-zinc-600 pt-2">{basisLabel.toUpperCase()}</li>
                            </ul>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
